import { Clock, LogOut, LogIn, Home } from 'lucide-react'
import { useCheckInOut } from '@/hooks/useCheckInOut'
import CheckinModal from '@/components/CheckinModal'
import CheckoutModal from '@/components/CheckoutModal'
import EarlyLeavePrompt from '@/components/EarlyLeavePrompt'

function fmtHHMM(hhmmss: string): string {
  return hhmmss.slice(0, 5)
}

// Drives the same check-in/out flow (carryover goals, WFH toggle, explicit
// time + approval note, early-leave reason, checkout goal review) as the
// employee "My Attendance" page — see useCheckInOut for the shared logic.
export default function CheckInOutCard() {
  const checkInOut = useCheckInOut()
  const { status, isCheckedIn, isCheckedOut } = checkInOut

  return (
    <div className="flex flex-col gap-4">
      {checkInOut.pendingMsg && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="flex-1 text-xs text-amber-800">{checkInOut.pendingMsg}</p>
          <button onClick={checkInOut.clearPendingMsg} className="text-amber-500 hover:text-amber-700 text-xs">✕</button>
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span className="flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {status?.effective_standup_time ? `Exp: ${fmtHHMM(status.effective_standup_time)}` : '— Exp: —'}
        </span>
        <span className="flex items-center gap-1.5">
          <LogOut className="h-3.5 w-3.5" />
          {status?.effective_eod_time ? `Exp: ${fmtHHMM(status.effective_eod_time)}` : '— Exp: —'}
        </span>
      </div>

      <div className="rounded-lg bg-gray-50 border border-gray-100 text-center py-3">
        <p className="text-2xl font-bold tabular-nums text-gray-800">
          {status?.total_worked_minutes
            ? `${String(Math.floor(status.total_worked_minutes / 60)).padStart(2, '0')}:${String(status.total_worked_minutes % 60).padStart(2, '0')}:00`
            : '00:00:00'}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {isCheckedIn ? 'Currently working' : isCheckedOut ? 'Checked out for today' : 'Not checked in yet'}
        </p>
      </div>

      {isCheckedIn ? (
        <button
          onClick={checkInOut.openCheckout}
          className="flex items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Mark Check Out
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => checkInOut.openCheckin()}
            className="flex items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-dark transition-colors"
          >
            <LogIn className="h-4 w-4" /> Mark Check In
          </button>
          <button
            onClick={() => checkInOut.openCheckin(true)}
            className="flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Home className="h-4 w-4" /> Check In WFH
          </button>
        </div>
      )}

      {checkInOut.showCheckin && checkInOut.checkinData && !checkInOut.checkinData.already_checked_in && (
        <CheckinModal
          carryoverGoals={checkInOut.checkinData.carryover_goals}
          onSubmit={checkInOut.submitCheckin}
          onClose={checkInOut.closeCheckin}
          isPending={checkInOut.checkinMutation.isPending}
        />
      )}

      {checkInOut.showEarlyLeavePrompt && (
        <EarlyLeavePrompt
          eodTime={status?.effective_eod_time ? fmtHHMM(status.effective_eod_time) : '17:00'}
          reason={checkInOut.earlyLeaveReason}
          onReasonChange={checkInOut.setEarlyLeaveReason}
          onCancel={checkInOut.cancelEarlyLeave}
          onContinue={checkInOut.confirmEarlyLeave}
        />
      )}

      {checkInOut.showCheckout && checkInOut.checkoutData && checkInOut.checkoutData.has_active_checkin && (
        <CheckoutModal
          todaysGoals={checkInOut.checkoutData.todays_goals}
          onSubmit={checkInOut.submitCheckout}
          onClose={checkInOut.closeCheckout}
          isPending={checkInOut.checkoutMutation.isPending}
          staleDate={checkInOut.checkoutData.stale_session_date}
          staleDefaultTime={checkInOut.checkoutData.stale_default_time}
          error={(checkInOut.checkoutMutation.error as any)?.response?.data?.detail}
        />
      )}
    </div>
  )
}
